import { useCallback, useEffect, useRef, useState } from 'react'
import {
	Alert,
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	InputAdornment,
	List,
	ListItemButton,
	TextField,
} from '@mui/material'
import { CameraAlt, ChevronRight, Close, Search } from '@mui/icons-material'
import ServicoAPI from '../../servicos/ServicoAPI'
import { buscarServicosLocal, mesclarResultados } from '../../servicos/buscaServicoExecucao'
import ScannerLeituraModal from './ScannerLeituraModal.jsx'

function MarcadorPonto({ ponto }) {
	return (
		<div
			className="flex items-center justify-center rounded-full border-2 shrink-0"
			style={{
				width: '1.75rem',
				height: '1.75rem',
				fontWeight: 700,
				fontSize: '0.85rem',
				borderColor: 'var(--primaria-pura)',
				color: 'var(--primaria-pura)',
			}}
		>
			{ponto || '-'}
		</div>
	)
}

export default function BuscarServicoModal({ open, onClose, servicos, dados, onSelecionar }) {
	const [termo, setTermo] = useState('')
	const [resultados, setResultados] = useState([])
	const [carregandoApi, setCarregandoApi] = useState(false)
	const [erroApi, setErroApi] = useState('')
	const [scannerAberto, setScannerAberto] = useState(false)

	const debounceRef = useRef(null)
	const requisicaoRef = useRef(0)

	const resetar = useCallback(() => {
		setTermo('')
		setResultados([])
		setCarregandoApi(false)
		setErroApi('')
		setScannerAberto(false)
		if (debounceRef.current) {
			clearTimeout(debounceRef.current)
			debounceRef.current = null
		}
	}, [])

	useEffect(() => {
		if (!open) resetar()
	}, [open, resetar])

	const buscarApi = useCallback(async (valor, imediato = false) => {
		const termoBusca = valor?.trim() ?? ''
		if (!termoBusca) return []
		if (!imediato && termoBusca.length < 3) return []

		const reqId = ++requisicaoRef.current
		setCarregandoApi(true)
		setErroApi('')

		try {
			const res = await ServicoAPI.pesquisarServicoExecucao(dados, termoBusca)
			if (reqId !== requisicaoRef.current) return []

			if (res?.resultado && Array.isArray(res.dados)) {
				return res.dados
			}

			if (res?.erro) setErroApi(res.erro)
			return []
		} catch {
			if (reqId === requisicaoRef.current) {
				setErroApi('Falha ao buscar número da nota.')
			}
			return []
		} finally {
			if (reqId === requisicaoRef.current) {
				setCarregandoApi(false)
			}
		}
	}, [dados])

	const executarBusca = useCallback(async (valor, { imediato = false } = {}) => {
		const termoBusca = valor?.trim() ?? ''
		const local = buscarServicosLocal(servicos, termoBusca)

		if (!termoBusca) {
			setResultados([])
			setErroApi('')
			return
		}

		setResultados(local)

		if (termoBusca.length >= 3 || imediato) {
			const api = await buscarApi(termoBusca, imediato)
			setResultados(mesclarResultados(local, api))
		}
	}, [buscarApi, servicos])

	const handleTermoChange = (event) => {
		const valor = event.target.value
		setTermo(valor)

		if (debounceRef.current) clearTimeout(debounceRef.current)

		const local = buscarServicosLocal(servicos, valor)
		setResultados(local)
		setErroApi('')

		if (!valor.trim()) {
			setCarregandoApi(false)
			return
		}

		if (valor.trim().length < 3) {
			setCarregandoApi(false)
			return
		}

		debounceRef.current = setTimeout(() => {
			executarBusca(valor)
		}, 300)
	}

	const handleCodigoLido = async (codigo) => {
		setScannerAberto(false)
		setTermo(codigo)
		await executarBusca(codigo, { imediato: true })
	}

	const handleSelecionar = (id) => {
		onSelecionar?.(id)
		onClose?.()
	}

	const semResultados = termo.trim().length > 0 && resultados.length === 0 && !carregandoApi

	return (
		<>
			<Dialog
				open={open && !scannerAberto}
				onClose={onClose}
				fullWidth
				maxWidth="sm"
				PaperProps={{ sx: { mx: 1 } }}
			>
				<DialogTitle className="flex items-center justify-between py-3">
					<span className="font-bold text-base">Buscar pedido ou endereço</span>
					<IconButton onClick={onClose} size="small" edge="end">
						<Close />
					</IconButton>
				</DialogTitle>

				<DialogContent className="pt-0 pb-2">
					<TextField
						fullWidth
						autoFocus
						placeholder="Serviço, endereço, nº nota ou observação"
						value={termo}
						onChange={handleTermoChange}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<Search />
								</InputAdornment>
							),
							endAdornment: (
								<InputAdornment position="end">
									<IconButton onClick={() => setScannerAberto(true)} edge="end" title="Escanear código">
										<CameraAlt />
									</IconButton>
								</InputAdornment>
							),
						}}
					/>

					{carregandoApi && (
						<div className="flex items-center gap-2 py-3 text-sm opacity-70">
							<CircularProgress size={18} />
							Buscando nº nota...
						</div>
					)}

					{erroApi && (
						<Alert severity="warning" className="mt-2">
							{erroApi}
						</Alert>
					)}

					{semResultados && (
						<Alert severity="info" className="mt-2">
							Nenhum serviço encontrado.
						</Alert>
					)}

					{resultados.length > 0 && (
						<List disablePadding className="mt-2 max-h-72 overflow-y-auto">
							{resultados.map((item, index) => (
								<div key={`${item.id}-${item.ponto}-${index}`}>
									{index > 0 && <Divider />}
									<ListItemButton onClick={() => handleSelecionar(item.id)} className="py-3 px-1">
										<div className="flex items-center gap-3 w-full min-w-0">
											{item.ponto > 0 && <MarcadorPonto ponto={item.ponto} />}
											<div className="flex-1 min-w-0">
												<div className="font-bold truncate">#{item.codigo ?? item.id}</div>
												<div className="text-sm truncate opacity-80">{item.endereco || 'Sem endereço'}</div>
												{item.numeroNota && (
													<div className="text-xs opacity-60 truncate">Nota: {item.numeroNota}</div>
												)}
											</div>
											<ChevronRight className="opacity-50 shrink-0" />
										</div>
									</ListItemButton>
								</div>
							))}
						</List>
					)}

					<Button
						fullWidth
						variant="contained"
						className="mt-4 mb-1"
						onClick={onClose}
						sx={{
							backgroundColor: 'var(--primaria-pura)',
							'&:hover': { backgroundColor: 'var(--primaria-darkest)' },
						}}
					>
						Fechar
					</Button>
				</DialogContent>
			</Dialog>

			<ScannerLeituraModal
				open={open && scannerAberto}
				onClose={() => setScannerAberto(false)}
				onCodigoLido={handleCodigoLido}
				elementId="scanner-busca-reader"
				zIndex={13000}
				titulo="Escanear para buscar"
				mensagemRodape="Aponte para o QR code ou código de barras. A busca será feita automaticamente."
				bloquearReleitura={false}
			/>
		</>
	)
}
